// CR036: Page Detail View Component
// CR431: Resolve page images from storage paths via batch URL signing

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ArrowLeft, Image, FileText, Target, Bot, CreditCard, AlertTriangle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { supabase } from '@/integrations/supabase/client';
import { useSignedStorageUrls } from '@/hooks/useSignedStorageUrls';
import type { NormalizedDeal, SignalEvaluation } from '@/lib/flyer-pipeline-v2/types';

interface PageDetailViewProps {
  runId: string;
  pageNumber: number;
  deals: NormalizedDeal[];
  onBack: () => void;
}

interface PageDetail {
  id: string;
  page_number: number;
  image_url: string | null;
  image_storage_path: string | null;
  thumbnail_storage_path: string | null;
  primary_ocr_engine: string | null;
  primary_ocr_text: string | null;
  primary_ocr_confidence: number | null;
  primary_ocr_score_total: number | null;
  primary_ocr_score_breakdown: Record<string, number> | null;
  candidate_for_ai: boolean;
  candidate_score: number | null;
  candidate_threshold: number | null;
  candidate_score_breakdown: SignalEvaluation[] | null;
  candidate_reason_codes: string[] | null;
  ai_status: string | null;
  ai_raw_output: unknown;
  ai_duration_ms: number | null;
  ai_model_used: string | null;
  ai_error: string | null;
  source_page_number: number | null;
  source_page_label: string | null;
}

export function PageDetailView({ runId, pageNumber, deals, onBack }: PageDetailViewProps) {
  const [pageDetail, setPageDetail] = useState<PageDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPageDetail = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('flyer_scan_pages_v2')
        .select('*')
        .eq('run_id', runId)
        .eq('page_number', pageNumber)
        .single();

      if (!error && data) {
        setPageDetail({
          id: data.id,
          page_number: data.page_number,
          image_url: data.image_url,
          image_storage_path: data.image_storage_path ?? null,
          thumbnail_storage_path: data.thumbnail_storage_path ?? null,
          primary_ocr_engine: data.primary_ocr_engine,
          primary_ocr_text: data.primary_ocr_text,
          primary_ocr_confidence: data.primary_ocr_confidence ? Number(data.primary_ocr_confidence) : null,
          primary_ocr_score_total: data.primary_ocr_score_total ? Number(data.primary_ocr_score_total) : null,
          primary_ocr_score_breakdown: data.primary_ocr_score_breakdown as Record<string, number> | null,
          candidate_for_ai: data.candidate_for_ai ?? false,
          candidate_score: data.candidate_score ? Number(data.candidate_score) : null,
          candidate_threshold: data.candidate_threshold ? Number(data.candidate_threshold) : null,
          candidate_score_breakdown: (Array.isArray(data.candidate_score_breakdown) ? data.candidate_score_breakdown : []) as unknown as SignalEvaluation[] | null,
          candidate_reason_codes: data.candidate_reason_codes ?? null,
          ai_status: data.ai_status,
          ai_raw_output: data.ai_raw_output,
          ai_duration_ms: data.ai_duration_ms,
          ai_model_used: data.ai_model_used,
          ai_error: data.ai_error,
          source_page_number: data.source_page_number ?? null,
          source_page_label: data.source_page_label ?? null,
        });
      }
      setLoading(false);
    };

    loadPageDetail();
  }, [runId, pageNumber]);

  // CR431: Batch-resolve storage paths for this page
  const { resolveUrl } = useSignedStorageUrls([
    pageDetail?.image_storage_path,
    pageDetail?.thumbnail_storage_path,
  ]);

  // CR431: Priority: image_storage_path (signed) > image_url (legacy)
  const resolvedImageUrl =
    resolveUrl(pageDetail?.image_storage_path)
    ?? pageDetail?.image_url
    ?? null;

  if (loading) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        Loading page details...
      </div>
    );
  }

  if (!pageDetail) {
    return (
      <div className="p-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <p className="text-center text-muted-foreground mt-4">Page not found.</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Pages
        </Button>
        <div className="flex items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Badge className="bg-primary text-primary-foreground font-semibold">
                  Batch Page #{pageNumber}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs max-w-[200px]">
                  Canonical page index for this scan run (used for identity, joins, metrics, and navigation).
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Badge variant="outline" className="text-xs font-mono">SSOT</Badge>
          {pageDetail.source_page_number != null && (
            <Badge variant="secondary" className="text-xs">
              Source p.{pageDetail.source_page_number}
            </Badge>
          )}
          {pageDetail.candidate_for_ai ? (
            <Badge className="bg-green-100 text-green-800">Candidate</Badge>
          ) : (
            <Badge variant="secondary">Not Candidate</Badge>
          )}
          <Badge variant="outline">
            AI: {pageDetail.ai_status}
          </Badge>
        </div>
      </div>

      <Tabs defaultValue="preview">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="preview" className="text-xs">
            <Image className="h-3 w-3 mr-1" />
            Preview
          </TabsTrigger>
          <TabsTrigger value="ocr" className="text-xs">
            <FileText className="h-3 w-3 mr-1" />
            OCR
          </TabsTrigger>
          <TabsTrigger value="scoring" className="text-xs">
            <Target className="h-3 w-3 mr-1" />
            Scoring
          </TabsTrigger>
          <TabsTrigger value="ai" className="text-xs">
            <Bot className="h-3 w-3 mr-1" />
            AI Output
          </TabsTrigger>
          <TabsTrigger value="deals" className="text-xs">
            <CreditCard className="h-3 w-3 mr-1" />
            Deals ({deals.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="preview" className="mt-4">
          <div className="border rounded-md overflow-hidden bg-muted/50">
            {resolvedImageUrl ? (
              <img
                src={resolvedImageUrl}
                alt={`Page ${pageNumber}`}
                className="w-full max-h-[500px] object-contain"
              />
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No image available
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="ocr" className="mt-4 space-y-4">
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center justify-between">
                Primary OCR Output
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{pageDetail.primary_ocr_engine}</Badge>
                  <Badge variant="outline">Score: {pageDetail.primary_ocr_score_total?.toFixed(0) ?? 'N/A'}</Badge>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[200px] border rounded-md p-2 bg-muted/30">
                <pre className="text-xs whitespace-pre-wrap font-mono">
                  {pageDetail.primary_ocr_text || 'No OCR text available'}
                </pre>
              </ScrollArea>
              {pageDetail.primary_ocr_score_breakdown && (
                <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                  {Object.entries(pageDetail.primary_ocr_score_breakdown).map(([key, value]) => (
                    <div key={key} className="flex justify-between bg-muted/30 rounded p-1 px-2">
                      <span className="text-muted-foreground">{key}:</span>
                      <span>{typeof value === 'number' ? value.toFixed(2) : value}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="scoring" className="mt-4 space-y-4">
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center justify-between">
                Candidate Scoring
                <div className="flex items-center gap-2">
                  <Badge variant={pageDetail.candidate_for_ai ? 'default' : 'secondary'}>
                    {pageDetail.candidate_score?.toFixed(1) ?? 0} / {pageDetail.candidate_threshold ?? 7}
                  </Badge>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-1">
                {pageDetail.candidate_reason_codes?.map((code) => (
                  <Badge key={code} variant="outline" className="text-xs">
                    {code}
                  </Badge>
                ))}
              </div>
              <div className="space-y-2">
                <h4 className="text-xs font-medium">Signal Breakdown</h4>
                {pageDetail.candidate_score_breakdown?.map((signal) => (
                  <div
                    key={signal.signal_id}
                    className={`p-2 rounded border text-xs ${
                      signal.fired
                        ? signal.delta >= 0
                          ? 'bg-green-50 border-green-200'
                          : 'bg-red-50 border-red-200'
                        : 'bg-muted/30 border-muted'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{signal.signal_name}</span>
                      <div className="flex items-center gap-2">
                        <Badge variant={signal.fired ? 'default' : 'secondary'} className="text-xs">
                          {signal.fired ? 'Fired' : 'Not Fired'}
                        </Badge>
                        <span className={signal.delta >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {signal.delta > 0 ? '+' : ''}
                          {signal.delta}
                        </span>
                      </div>
                    </div>
                    {signal.evidence.length > 0 && (
                      <div className="mt-1 text-muted-foreground">
                        {signal.evidence.join(', ')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ai" className="mt-4 space-y-4">
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center justify-between">
                AI Scan Output
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{pageDetail.ai_model_used || 'N/A'}</Badge>
                  {pageDetail.ai_duration_ms && (
                    <Badge variant="outline">{pageDetail.ai_duration_ms}ms</Badge>
                  )}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="p-2 bg-muted/50 border rounded text-xs space-y-1">
                <div className="font-medium text-muted-foreground">Page Context</div>
                <div className="flex items-center gap-3">
                  <span>
                    <span className="font-mono font-semibold">SSOT:</span> Batch Page #{pageNumber}
                  </span>
                  {pageDetail.source_page_number != null && (
                    <span>
                      <span className="font-mono font-semibold">Source:</span> p.{pageDetail.source_page_number}
                    </span>
                  )}
                </div>
                {(() => {
                  const rawOutput = pageDetail.ai_raw_output as Record<string, unknown> | null;
                  const modelReported = (rawOutput as any)?.model_response?.choices?.[0]?.message?.content;
                  let modelPageNum: number | null = null;
                  try {
                    if (typeof modelReported === 'string') {
                      const parsed = JSON.parse(modelReported);
                      const deals = parsed?.deals || (Array.isArray(parsed) ? parsed : []);
                      if (deals.length > 0 && deals[0]?.page_number != null) {
                        modelPageNum = deals[0].page_number;
                      }
                    }
                  } catch { /* ignore */ }

                  if (modelPageNum != null && pageDetail.source_page_number != null && modelPageNum !== pageDetail.source_page_number) {
                    return (
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <AlertTriangle className="h-3 w-3 text-warning" />
                        <span>Model reported page_number={modelPageNum} <span className="italic">(ignored)</span></span>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>

              {pageDetail.ai_error && (
                <div className="p-2 bg-destructive/10 border border-destructive/30 rounded text-xs text-destructive">
                  Error: {pageDetail.ai_error}
                </div>
              )}
              <ScrollArea className="h-[200px] border rounded-md p-2 bg-muted/30">
                <pre className="text-xs whitespace-pre-wrap font-mono">
                  {pageDetail.ai_raw_output
                    ? JSON.stringify(pageDetail.ai_raw_output, null, 2)
                    : pageDetail.ai_status === 'skipped'
                    ? 'AI scan was skipped (page is not a candidate)'
                    : 'No AI output available'}
                </pre>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="deals" className="mt-4 space-y-4">
          {deals.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              No deals detected on this page.
            </div>
          ) : (
            <div className="space-y-3">
              {deals.map((deal) => (
                <Card key={deal.deal_id}>
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge variant={deal.deal_type === 'UNCLASSIFIED' ? 'destructive' : 'default'}>
                            {deal.deal_type}
                          </Badge>
                          <Badge variant="outline">{deal.status}</Badge>
                          <span className="text-xs text-muted-foreground">
                            Confidence: {(deal.confidence * 100).toFixed(0)}%
                          </span>
                        </div>
                        <div className="mt-2 text-sm">
                          {deal.purchase.brand && (
                            <p>
                              <span className="text-muted-foreground">Purchase:</span>{' '}
                              {deal.purchase.brand} @ ${deal.purchase.amount}
                            </p>
                          )}
                          {deal.reward.amount > 0 && (
                            <p>
                              <span className="text-muted-foreground">Reward:</span>{' '}
                              ${deal.reward.amount} {deal.reward.merchant && `at ${deal.reward.merchant}`}
                            </p>
                          )}
                          {deal.validity.raw_text && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Validity: {deal.validity.raw_text}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}